import React, { useMemo, useState, Fragment } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, AlertTriangle, ArrowRight, Target, ChevronRight, Building2, MapPin, Globe, TrendingDown, Users, ChevronDown, ChevronUp, Sparkles } from 'lucide-react';
import { MOCK_RECOMMENDATIONS, Recommendation } from '../../data/mockRecommendationsData';
import { REGIONS, PROJECT_REGION_MAP } from '../../data/regionData';
import { RecommendationDetailModal } from './RecommendationDetailModal';
type Level = 'enterprise' | 'region' | 'project';
/** Highlights key data points in rationale text with bold + underline */
function formatInsightText(text: string): React.ReactNode {
  // Match: numbers with context (e.g. "6 fall-protection observations"), percentages, OSHA refs, day/week counts
  const pattern = /(\d+%|\d+\s+(?:fall-protection|ladder-use|struck-by|dust-related|scaffold|near[\s-]miss|lift plan|observation|inspection|PTP|cord|trench|mark-out|entry|worker|project|crew|item|site|case|day|hour|week|month|level|condition|discrepanc)\w*(?:\s+\w+){0,2})|(\b29\s*CFR\s*[\d.]+\b)|(\bOSHA\b(?:\s+\w+){0,3})|(\b(?:top|bottom)\s+decile\b)/gi;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  const regex = new RegExp(pattern);
  const globalRegex = new RegExp(pattern.source, 'gi');
  while ((match = globalRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    parts.push(<span key={match.index} className="font-semibold underline decoration-gray-300 underline-offset-2" data-id="element-2078">
        {match[0]}
      </span>);
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts.length > 0 ? parts : text;
}
export function RecommendationsView() {
  const [projectSearch, setProjectSearch] = useState('');
  const [currentLevel, setCurrentLevel] = useState<Level>('enterprise');
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
  const [selectedProject, setSelectedProject] = useState<string | null>(null);
  const [expandedRecs, setExpandedRecs] = useState<Set<string>>(new Set());
  const [visibleCount, setVisibleCount] = useState(3);
  const [expandedBUs, setExpandedBUs] = useState<Set<string>>(new Set());
  const [selectedRecommendation, setSelectedRecommendation] = useState<Recommendation | null>(null);
  // Recommendation counts for badges
  const enterpriseRecCount = useMemo(() => MOCK_RECOMMENDATIONS.filter(r => r.scope === 'systemic').length, []);
  const recCountsByRegion = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const r of REGIONS) {
      counts[r.id] = MOCK_RECOMMENDATIONS.filter(rec => rec.scope === 'region' && rec.regionId === r.id).length;
    }
    return counts;
  }, []);
  const recCountsByProject = useMemo(() => {
    const counts: Record<string, number> = {};
    // Calculate for all projects to show in the tree
    for (const p of PROJECT_REGION_MAP) {
      counts[p.projectName] = MOCK_RECOMMENDATIONS.filter(rec => rec.scope === 'project' && rec.affectedProjects.includes(p.projectName)).length;
    }
    return counts;
  }, []);
  const toggleExpand = (id: string) => {
    setExpandedRecs(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);else next.add(id);
      return next;
    });
  };
  const toggleBUExpand = (regionId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedBUs(prev => {
      const next = new Set(prev);
      if (next.has(regionId)) next.delete(regionId);else next.add(regionId);
      return next;
    });
  };
  const filteredRecommendations = useMemo(() => {
    return MOCK_RECOMMENDATIONS.filter(rec => {
      // Level Filtering
      if (currentLevel === 'enterprise') {
        return rec.scope === 'systemic';
      }
      if (currentLevel === 'region') {
        return rec.scope === 'region' && rec.regionId === selectedRegion;
      }
      if (currentLevel === 'project') {
        return rec.scope === 'project' && rec.affectedProjects.includes(selectedProject!);
      }
      return true;
    }).sort((a, b) => {
      // Sort by risk reduction descending (highest impact first)
      return b.riskReduction - a.riskReduction;
    });
  }, [currentLevel, selectedRegion, selectedProject]);
  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case 'critical':
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold bg-red-100 text-red-700 border border-red-200 uppercase tracking-wider" data-id="element-2079">
            <AlertTriangle size={10} data-id="element-2080" /> Critical
          </span>;
      case 'high':
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold bg-amber-100 text-amber-700 border border-amber-200 uppercase tracking-wider" data-id="element-2081">
            High
          </span>;
      case 'medium':
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold bg-yellow-100 text-yellow-700 border border-yellow-200 uppercase tracking-wider" data-id="element-2082">
            Medium
          </span>;
      case 'low':
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold bg-emerald-100 text-emerald-700 border border-emerald-200 uppercase tracking-wider" data-id="element-2083">
            Low
          </span>;
      default:
        return null;
    }
  };
  const handleEnterpriseClick = () => {
    setCurrentLevel('enterprise');
    setSelectedRegion(null);
    setSelectedProject(null);
    setVisibleCount(3);
  };
  const handleRegionClick = (regionId: string) => {
    setCurrentLevel('region');
    setSelectedRegion(regionId);
    setSelectedProject(null);
    setVisibleCount(3);
    // Auto-expand the BU when selected
    setExpandedBUs(prev => {
      const next = new Set(prev);
      next.add(regionId);
      return next;
    });
  };
  const handleProjectClick = (projectName: string, regionId: string) => {
    setCurrentLevel('project');
    setSelectedProject(projectName);
    setSelectedRegion(regionId);
    setVisibleCount(3);
  };
  const regionName = REGIONS.find(r => r.id === selectedRegion)?.name;
  return <div className="flex flex-row h-full bg-gray-50/50" data-id="element-2084">
      {/* Left Sidebar Navigation */}
      <div className="w-[280px] flex-shrink-0 bg-white border-r border-gray-200 flex flex-col h-full" data-id="element-2085">
        <div className="p-4 border-b border-gray-100" data-id="element-2086">
          <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider" data-id="element-2087">
            Scope
          </h3>
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-1 no-scrollbar" data-id="element-2088">
          {/* Enterprise Node */}
          <button onClick={handleEnterpriseClick} className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-colors border-l-4 ${currentLevel === 'enterprise' ? 'bg-amber-50 text-amber-800 border-amber-500' : 'text-gray-700 hover:bg-gray-50 border-transparent'}`} data-id="element-2089">
            <div className="flex items-center gap-2" data-id="element-2090">
              <Globe size={16} className={currentLevel === 'enterprise' ? 'text-amber-600' : 'text-gray-400'} data-id="element-2091" />
              Enterprise
            </div>
            <span className={`inline-flex items-center justify-center min-w-[20px] h-[20px] px-1 rounded-full text-[10px] font-bold ${currentLevel === 'enterprise' ? 'bg-amber-200 text-amber-800' : 'bg-gray-100 text-gray-500'}`} data-id="element-2092">
              {enterpriseRecCount}
            </span>
          </button>

          {/* Business Units */}
          {REGIONS.map(region => {
          const isExpanded = expandedBUs.has(region.id);
          const isSelected = currentLevel === 'region' && selectedRegion === region.id;
          const regionProjectsList = PROJECT_REGION_MAP.filter(p => p.regionId === region.id).map(p => p.projectName).sort();
          return <div key={region.id} className="space-y-0.5 mt-1" data-id="element-2093">
                <button onClick={() => handleRegionClick(region.id)} className={`w-full flex items-center justify-between pr-3 py-2 rounded-lg text-sm font-medium transition-colors border-l-4 ${isSelected ? 'bg-amber-50 text-amber-800 border-amber-500' : 'text-gray-700 hover:bg-gray-50 border-transparent'}`} data-id="element-2094">
                  <div className="flex items-center gap-1.5 pl-1" data-id="element-2095">
                    <div onClick={e => toggleBUExpand(region.id, e)} className="p-1 rounded hover:bg-gray-200 transition-colors cursor-pointer text-gray-400 hover:text-gray-600" data-id="element-2096">
                      {isExpanded ? <ChevronDown size={14} data-id="element-2097" /> : <ChevronRight size={14} data-id="element-2098" />}
                    </div>
                    <MapPin size={16} className={isSelected ? 'text-amber-600' : 'text-gray-400'} data-id="element-2099" />
                    <span className="truncate text-left" data-id="element-2100">{region.name}</span>
                  </div>
                  <span className={`inline-flex items-center justify-center min-w-[20px] h-[20px] px-1 rounded-full text-[10px] font-bold ${isSelected ? 'bg-amber-200 text-amber-800' : 'bg-gray-100 text-gray-500'}`} data-id="element-2101">
                    {recCountsByRegion[region.id] ?? 0}
                  </span>
                </button>

                {/* Projects */}
                <AnimatePresence data-id="element-2102">
                  {isExpanded && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} className="overflow-hidden" data-id="element-2103">
                      <div className="pl-8 pr-2 py-1 space-y-0.5" data-id="element-2104">
                        {regionProjectsList.map(project => {
                    const isProjectSelected = currentLevel === 'project' && selectedProject === project;
                    return <button key={project} onClick={() => handleProjectClick(project, region.id)} className={`w-full flex items-center justify-between px-3 py-1.5 rounded-lg text-sm transition-colors border-l-2 ${isProjectSelected ? 'bg-amber-50 text-amber-800 font-medium border-amber-500' : 'text-gray-600 hover:bg-gray-50 border-transparent'}`} data-id="element-2105">
                              <div className="flex flex-col min-w-0" data-id="element-2106">
                                <div className="flex items-center gap-2 truncate" data-id="element-2107">
                                  <Building2 size={14} className={`shrink-0 ${isProjectSelected ? 'text-amber-600' : 'text-gray-400'}`} data-id="element-2108" />
                                  <span className="truncate text-left" data-id="element-2109">
                                    {project}
                                  </span>
                                </div>
                                {(() => {
                          const mapping = PROJECT_REGION_MAP.find(p => p.projectName === project);
                          return mapping ? <span className="text-[10px] text-gray-400 ml-6 truncate text-left" data-id="element-2110">
                                      {mapping.city}, {mapping.state}
                                    </span> : null;
                        })()}
                              </div>
                              <span className={`inline-flex items-center justify-center min-w-[20px] h-[20px] px-1 rounded-full text-[10px] font-bold shrink-0 ${isProjectSelected ? 'bg-amber-200 text-amber-800' : 'bg-gray-100 text-gray-500'}`} data-id="element-2111">
                                {recCountsByProject[project] ?? 0}
                              </span>
                            </button>;
                  })}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>;
        })}
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 overflow-y-auto p-6" data-id="element-2112">
        <div className="max-w-6xl mx-auto space-y-4" data-id="element-2113">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-1.5 text-sm mb-1" data-id="element-2114">
            <button onClick={handleEnterpriseClick} className={`flex items-center gap-1.5 px-2 py-1 rounded-md transition-colors ${currentLevel === 'enterprise' ? 'font-bold text-amber-800 bg-amber-50' : 'font-medium text-gray-500 hover:text-gray-800 hover:bg-gray-100'}`} data-id="element-2115">
              <Globe size={14} data-id="element-2116" />
              Enterprise
            </button>
            {(currentLevel === 'region' || currentLevel === 'project') && regionName && <>
                  <ChevronRight size={14} className="text-gray-300" data-id="element-2117" />
                  <button onClick={() => handleRegionClick(selectedRegion!)} className={`flex items-center gap-1.5 px-2 py-1 rounded-md transition-colors ${currentLevel === 'region' ? 'font-bold text-amber-800 bg-amber-50' : 'font-medium text-gray-500 hover:text-gray-800 hover:bg-gray-100'}`} data-id="element-2118">
                    <MapPin size={14} data-id="element-2119" />
                    {regionName}
                  </button>
                </>}
            {currentLevel === 'project' && selectedProject && <>
                <ChevronRight size={14} className="text-gray-300" data-id="element-2120" />
                <span className="flex items-center gap-1.5 px-2 py-1 rounded-md font-bold text-amber-800 bg-amber-50" data-id="element-2121">
                  <Building2 size={14} data-id="element-2122" />
                  {selectedProject}
                </span>
              </>}
          </nav>

          <div className="mb-6" data-id="element-2123">
            {(() => {
            // Per-level styling
            const levelStyles = {
              enterprise: {
                bg: 'bg-indigo-50/70',
                border: 'border-indigo-200',
                label: 'ENTERPRISE',
                labelBg: 'bg-indigo-600',
                labelText: 'text-white',
                sparkle: 'text-indigo-500',
                textColor: 'text-indigo-900/80',
                accentBar: 'bg-indigo-500'
              },
              region: {
                bg: 'bg-teal-50/70',
                border: 'border-teal-200',
                label: 'BUSINESS UNIT',
                labelBg: 'bg-teal-600',
                labelText: 'text-white',
                sparkle: 'text-teal-500',
                textColor: 'text-teal-900/80',
                accentBar: 'bg-teal-500'
              },
              project: {
                bg: 'bg-amber-50/70',
                border: 'border-amber-200',
                label: 'PROJECT',
                labelBg: 'bg-amber-600',
                labelText: 'text-white',
                sparkle: 'text-amber-500',
                textColor: 'text-amber-900/80',
                accentBar: 'bg-amber-500'
              }
            };
            // Per-region color accents for within-level distinction
            const regionAccents: Record<string, {
              ring: string;
              nameBg: string;
              nameText: string;
            }> = {
              northeast: {
                ring: 'ring-blue-300',
                nameBg: 'bg-blue-100',
                nameText: 'text-blue-800'
              },
              mid_atlantic: {
                ring: 'ring-violet-300',
                nameBg: 'bg-violet-100',
                nameText: 'text-violet-800'
              },
              southeast: {
                ring: 'ring-rose-300',
                nameBg: 'bg-rose-100',
                nameText: 'text-rose-800'
              },
              midwest: {
                ring: 'ring-emerald-300',
                nameBg: 'bg-emerald-100',
                nameText: 'text-emerald-800'
              },
              southwest: {
                ring: 'ring-orange-300',
                nameBg: 'bg-orange-100',
                nameText: 'text-orange-800'
              },
              west_coast: {
                ring: 'ring-cyan-300',
                nameBg: 'bg-cyan-100',
                nameText: 'text-cyan-800'
              }
            };
            const style = levelStyles[currentLevel];
            const regionAccent = selectedRegion ? regionAccents[selectedRegion] || regionAccents.northeast : null;
            const projectRegionId = selectedProject ? PROJECT_REGION_MAP.find(p => p.projectName === selectedProject)?.regionId : null;
            const projectRegionAccent = projectRegionId ? regionAccents[projectRegionId] || null : null;
            const projectCity = selectedProject ? PROJECT_REGION_MAP.find(p => p.projectName === selectedProject)?.city : null;
            const projectState = selectedProject ? PROJECT_REGION_MAP.find(p => p.projectName === selectedProject)?.state : null;
            return <div className={`relative overflow-hidden rounded-xl border ${style.border} ${style.bg}`} data-id="element-2124">
                  {/* Accent bar on left */}
                  <div className={`absolute left-0 top-0 bottom-0 w-1 ${style.accentBar}`} data-id="element-2125" />

                  <div className="pl-5 pr-4 py-4" data-id="element-2126">
                    {/* Top row: level label + unit name */}
                    <div className="flex items-center gap-2.5 mb-2" data-id="element-2127">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold tracking-wider ${style.labelBg} ${style.labelText}`} data-id="element-2128">
                        {style.label}
                      </span>
                      {currentLevel === 'enterprise' && <span className="text-base font-bold text-gray-900" data-id="element-2129">
                          All Projects
                        </span>}
                      {currentLevel === 'region' && regionName && regionAccent && <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-lg text-sm font-bold ring-1 ${regionAccent.ring} ${regionAccent.nameBg} ${regionAccent.nameText}`} data-id="element-2130">
                            <MapPin size={13} data-id="element-2131" />
                            {regionName}
                          </span>}
                      {currentLevel === 'project' && selectedProject && <div className="flex items-center gap-2" data-id="element-2132">
                          <span className="text-base font-bold text-gray-900" data-id="element-2133">
                            {selectedProject}
                          </span>
                          {projectCity && projectState && <span className="text-xs text-gray-500" data-id="element-2134">
                              {projectCity}, {projectState}
                            </span>}
                          {projectRegionAccent && regionName && <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold ring-1 ${projectRegionAccent.ring} ${projectRegionAccent.nameBg} ${projectRegionAccent.nameText}`} data-id="element-2135">
                              <MapPin size={10} data-id="element-2136" />
                              {regionName}
                            </span>}
                        </div>}
                    </div>

                    {/* AI message */}
                    <div className="flex items-start gap-2" data-id="element-2137">
                      <Sparkles size={13} className={`${style.sparkle} mt-0.5 shrink-0`} data-id="element-2138" />
                      <p className={`text-sm ${style.textColor}`} data-id="element-2139">
                        {currentLevel === 'enterprise' ? <>
                            Across your entire portfolio of{' '}
                            <span className="font-semibold" data-id="element-2140">
                              {PROJECT_REGION_MAP.length} projects
                            </span>
                            , I identified systemic patterns in risk trends,
                            observations, and compliance data — here's what
                            stands out.
                          </> : currentLevel === 'region' ? <>
                            Across the{' '}
                            <span className="font-semibold" data-id="element-2141">
                              {PROJECT_REGION_MAP.filter(p => p.regionId === selectedRegion).length}{' '}
                              projects
                            </span>{' '}
                            in this business unit, I identified regional
                            patterns in risk trends and observations — here's
                            what stands out.
                          </> : <>
                            I reviewed recent risk trends, observations, and
                            inspection findings for this project — here's what I
                            recommend.
                          </>}
                      </p>
                    </div>
                  </div>
                </div>;
          })()}
          </div>

          {filteredRecommendations.length === 0 ? <div className="text-center py-12 bg-white rounded-xl border border-gray-200" data-id="element-2142">
              <Target size={32} className="mx-auto text-gray-300 mb-3" data-id="element-2143" />
              <h3 className="text-gray-900 font-medium" data-id="element-2144">
                No recommendations found
              </h3>
              <p className="text-gray-500 text-sm mt-1" data-id="element-2145">
                {currentLevel === 'project' ? 'This project currently has no specific recommendations.' : 'No recommendations available at this level.'}
              </p>
            </div> : <div className="space-y-4" data-id="element-2146">
              <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm" data-id="element-2147">
                <table className="w-full text-left text-sm" data-id="element-2148">
                  <thead className="bg-gray-50 border-b border-gray-200" data-id="element-2149">
                    <tr data-id="element-2150">
                      <th className="px-4 py-3 font-bold text-gray-900 w-1/4" data-id="element-2151">
                        Insight
                      </th>
                      <th className="px-4 py-3 font-bold text-gray-900 w-1/4" data-id="element-2152">
                        Corrective Action
                      </th>
                      <th className="px-4 py-3 font-bold text-gray-900 text-center w-28" data-id="element-2153">
                        Risk Reduction
                      </th>
                      <th className="px-4 py-3 font-bold text-gray-900 w-36" data-id="element-2154">
                        Affected
                      </th>
                      <th className="px-4 py-3 font-bold text-gray-900 w-48" data-id="element-2155">
                        Linked Drivers
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100" data-id="element-2156">
                    {filteredRecommendations.slice(0, visibleCount).map(rec => <Fragment key={rec.id} data-id="element-2157">
                          <tr className="hover:bg-gray-50 transition-colors group" data-id="element-2158">
                            {/* Insight (rationale + trend) */}
                            <td className="px-4 py-4 align-top" data-id="element-2159">
                              <div className="text-xs text-gray-600 leading-relaxed" data-id="element-2160">
                                {formatInsightText(rec.rationale)}
                              </div>
                            </td>
                            {/* Recommendation */}
                            <td className="px-4 py-4 align-top" data-id="element-2161">
                              <div className="font-bold text-gray-900 mb-1" data-id="element-2162">
                                {rec.title}
                              </div>
                              <div className="mt-2" data-id="element-2163">
                                <button onClick={() => setSelectedRecommendation(rec)} className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-gray-900 bg-amber-400 hover:bg-amber-500 px-2.5 py-1 rounded-md shadow-sm transition-colors" data-id="element-2164">
                                  <Sparkles size={11} data-id="element-2165" />
                                  Discuss with Bilti
                                </button>
                              </div>
                            </td>
                            {/* Risk Reduction */}
                            <td className="px-4 py-4 align-top text-center" data-id="element-2166">
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 w-fit" data-id="element-2167">
                                <TrendingDown size={12} data-id="element-2168" />-{rec.riskReduction}%
                                Risk
                              </span>
                            </td>
                            {/* Affected */}
                            <td className="px-4 py-4 align-top" data-id="element-2169">
                              <div className="flex flex-col gap-1.5" data-id="element-2170">
                                {(rec.scope === 'systemic' || rec.scope === 'region') && rec.projectsAffected ? (() => {
                          const projects = rec.affectedProjects.includes('All Projects') ? PROJECT_REGION_MAP.map(p => p.projectName) : rec.affectedProjects;
                          const buBreakdown: Record<string, string[]> = {};
                          for (const proj of projects) {
                            const mapping = PROJECT_REGION_MAP.find(p => p.projectName === proj);
                            const buName = mapping ? REGIONS.find(r => r.id === mapping.regionId)?.name || mapping.regionId : 'Unknown';
                            if (!buBreakdown[buName]) buBreakdown[buName] = [];
                            buBreakdown[buName].push(proj);
                          }
                          const buCount = Object.keys(buBreakdown).length;
                          const tooltipLines = Object.entries(buBreakdown).sort((a, b) => b[1].length - a[1].length).map(([bu, projs]) => `${bu}: ${projs.length} project${projs.length !== 1 ? 's' : ''}`).join('\n');
                          const buEntries = Object.entries(buBreakdown).sort((a, b) => b[1].length - a[1].length);
                          return <div className="relative group/bu w-fit" data-id="element-2171">
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-700 border border-blue-100 w-fit cursor-default" data-id="element-2172">
                                          <Building2 size={10} data-id="element-2173" />
                                          {rec.projectsAffected} projects ·{' '}
                                          {buCount} BU{buCount !== 1 ? 's' : ''}
                                        </span>
                                        <div className="absolute left-0 bottom-full mb-1.5 z-50 hidden group-hover/bu:block" data-id="element-2174">
                                          <div className="bg-gray-900 text-white text-[10px] rounded-lg px-3 py-2 shadow-lg whitespace-nowrap space-y-0.5" data-id="element-2175">
                                            <div className="font-bold text-gray-300 mb-1" data-id="element-2176">
                                              Projects by Business Unit
                                            </div>
                                            {buEntries.map(([bu, projs]) => <div key={bu} className="flex items-center justify-between gap-4" data-id="element-2177">
                                                <span className="text-gray-200" data-id="element-2178">
                                                  {bu}
                                                </span>
                                                <span className="font-bold text-white" data-id="element-2179">
                                                  {projs.length}
                                                </span>
                                              </div>)}
                                          </div>
                                        </div>
                                      </div>;
                        })() : rec.contractorsAffected ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-700 border border-blue-100 w-fit" data-id="element-2180">
                                    <Users size={10} data-id="element-2181" />
                                    {rec.contractorsAffected} contractors
                                  </span> : null}
                              </div>
                            </td>
                            {/* Linked Drivers */}
                            <td className="px-4 py-4 align-top" data-id="element-2182">
                              <div className="flex flex-wrap gap-1.5" data-id="element-2183">
                                {rec.linkedDrivers.map(driver => <span key={driver} className="text-[10px] font-medium text-amber-700 bg-amber-50 border border-amber-100 px-1.5 py-0.5 rounded" data-id="element-2184">
                                    {driver}
                                  </span>)}
                              </div>
                            </td>
                          </tr>

                          {/* Expanded Row */}
                          <AnimatePresence data-id="element-2185">
                            {expandedRecs.has(rec.id) && rec.activityExamples && <tr className="bg-gray-50 border-b border-gray-200" data-id="element-2186">
                                  <td colSpan={5} className="p-0" data-id="element-2187">
                                    <motion.div initial={{
                          opacity: 0,
                          height: 0
                        }} animate={{
                          opacity: 1,
                          height: 'auto'
                        }} exit={{
                          opacity: 0,
                          height: 0
                        }} className="overflow-hidden" data-id="element-2188">
                                      <div className="px-6 py-4" data-id="element-2189">
                                        <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3" data-id="element-2190">
                                          Affected Activity Examples
                                        </h4>
                                        <div className="space-y-2" data-id="element-2191">
                                          {rec.activityExamples.map((ex, idx) => <div key={idx} className="bg-white border border-gray-200 rounded-lg p-3 flex items-center gap-4 shadow-sm" data-id="element-2192">
                                                <div className="flex-1 min-w-0" data-id="element-2193">
                                                  <div className="flex items-center gap-2 mb-1" data-id="element-2194">
                                                    <span className="text-sm font-bold text-gray-900 truncate" data-id="element-2195">
                                                      {ex.activityName}
                                                    </span>
                                                    {rec.scope !== 'project' && <span className="text-[10px] font-medium text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded truncate" data-id="element-2196">
                                                        {ex.projectName}
                                                      </span>}
                                                  </div>
                                                  <div className="flex items-center gap-3 text-xs text-gray-500" data-id="element-2197">
                                                    <span className="flex items-center gap-1" data-id="element-2198">
                                                      <Users size={12} data-id="element-2199" />{' '}
                                                      {ex.contractor}
                                                    </span>
                                                    <span className="flex items-center gap-1" data-id="element-2200">
                                                      <MapPin size={12} data-id="element-2201" />{' '}
                                                      {ex.floor}
                                                    </span>
                                                  </div>
                                                  <p className="text-xs text-gray-600 mt-2" data-id="element-2202">
                                                    {ex.explanation}
                                                  </p>
                                                </div>
                                                <div className="flex items-center gap-2 flex-shrink-0 bg-gray-50 px-3 py-2 rounded-lg border border-gray-100" data-id="element-2203">
                                                  <div className={`px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider border ${ex.currentRisk === 'critical' ? 'bg-red-50 text-red-700 border-red-200' : ex.currentRisk === 'high' ? 'bg-amber-50 text-amber-700 border-amber-200' : ex.currentRisk === 'medium' ? 'bg-yellow-50 text-yellow-700 border-yellow-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`} data-id="element-2204">
                                                    {ex.currentRisk}
                                                  </div>
                                                  <ArrowRight size={14} className="text-gray-400" data-id="element-2205" />
                                                  <div className={`px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider border ${ex.riskAfter === 'critical' ? 'bg-red-50 text-red-700 border-red-200' : ex.riskAfter === 'high' ? 'bg-amber-50 text-amber-700 border-amber-200' : ex.riskAfter === 'medium' ? 'bg-yellow-50 text-yellow-700 border-yellow-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`} data-id="element-2206">
                                                    {ex.riskAfter}
                                                  </div>
                                                </div>
                                              </div>)}
                                        </div>
                                      </div>
                                    </motion.div>
                                  </td>
                                </tr>}
                          </AnimatePresence>
                        </Fragment>)}
                  </tbody>
                </table>
              </div>

              {visibleCount < filteredRecommendations.length && <div className="pt-4 flex justify-center" data-id="element-2207">
                  <button onClick={() => setVisibleCount(prev => prev + 3)} className="px-6 py-2.5 bg-white border border-amber-200 text-amber-700 text-sm font-bold rounded-lg hover:bg-amber-50 hover:border-amber-300 transition-colors shadow-sm" data-id="element-2208">
                    Show 3 More ({filteredRecommendations.length - visibleCount}{' '}
                    remaining)
                  </button>
                </div>}
            </div>}
        </div>
      </div>

      <RecommendationDetailModal recommendation={selectedRecommendation} onClose={() => setSelectedRecommendation(null)} data-id="element-2209" />
    </div>;
}